import {
  AfterViewInit,
  Component,
  ElementRef,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import TomSelect from 'tom-select';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-tom-select',
  templateUrl: './tom-select.component.html',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TomSelectComponent),
      multi: true
    }
  ]
})
export class TomSelectComponent implements AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {

  @ViewChild('selectEl') selectEl!: ElementRef;
  @Input() options: any = {};
  @Input() multiple: boolean = false;
  @Input() selectClass: string = 'form-select';
  @Input() inputId: string | null = null;
  @Input() preloadOptions: Array<{ value: string; text: string; description?: string }> = [];

  private tom: TomSelect | null = null;
  private observer: MutationObserver | null = null;
  private currentValue: any = null;
  private disabled = false;

  onChange = (value: any) => {};
  onTouched = () => {};

  ngAfterViewInit() {
    this.initTom();
    this.observeDomChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['preloadOptions'] && !changes['options']) {
      return;
    }

    this.refreshOptions();
  }

  ngOnDestroy() {
    this.observer?.disconnect();
    this.tom?.destroy();
    this.tom = null;
  }

  private initTom() {
    this.tom = new TomSelect(this.selectEl.nativeElement, {
      maxItems: this.multiple ? null : 1,
      dropdownParent: 'body',
      searchField: [this.options?.labelField || 'text', 'description'],
      ...this.options,
      render: {
        option: (item: Record<string, unknown>, escape: (value: string) => string) => {
          const title = escape(String(item[this.options?.labelField || 'text'] ?? ''));
          const description = item['description'] ? escape(String(item['description'])) : '';
          return description
            ? `<div><span class="d-block">${title}</span><small class="d-block text-secondary">${description}</small></div>`
            : `<div>${title}</div>`;
        },
        item: (item: Record<string, unknown>, escape: (value: string) => string) =>
          `<div>${escape(String(item[this.options?.labelField || 'text'] ?? ''))}</div>`,
        ...this.options?.render,
      },
      wrapperClass: `${this.options?.wrapperClass || 'ts-wrapper'} app-tom-select-shell`,
      controlClass: `${this.options?.controlClass || 'ts-control'} app-tom-select-control`,
      options: this.mergePreloadOptions(this.options?.options),
      onChange: (value: any) => {
        this.currentValue = value;
        this.onChange(value);
      },
      onBlur: () => this.onTouched(),
    });
    if (this.currentValue !== null && this.currentValue !== undefined) {
      this.tom.setValue(this.currentValue, true);
    }
    if (this.disabled) {
      this.tom.disable();
    }
  }

  private observeDomChanges() {
    this.observer = new MutationObserver(() => {
      if (this.optionCatalogChanged()) {
        this.refreshOptions();
      }
    });

    this.observer.observe(this.selectEl.nativeElement, {
      childList: true,
      subtree: true
    });
  }

  private optionCatalogChanged(): boolean {
    if (!this.tom) return false;

    const valueField = this.options?.valueField || 'value';
    const labelField = this.options?.labelField || 'text';
    const domOptions = new Set(
      [
        ...Array.from(this.selectEl.nativeElement.options as HTMLOptionsCollection).map((option) => ({
          value: String(option.value),
          text: String(option.text),
        })),
        ...this.mergePreloadOptions(this.options?.options),
      ].map((option) => `${option.value}\u0000${option.text}`),
    );
    const tomOptions = new Set(
      Object.values((this.tom as any).options || {}).map((option: any) => (
        `${String(option?.[valueField] ?? '')}\u0000${String(option?.[labelField] ?? option?.text ?? '')}`
      )),
    );

    if (domOptions.size !== tomOptions.size) return true;
    return Array.from(domOptions).some((option) => !tomOptions.has(option));
  }

  private refreshOptions() {
    if (!this.tom) return;

    const tom = this.tom;
    const select = this.selectEl.nativeElement;

    tom.clearOptions();

    const catalog = new Map<string, { value: string; text: string; description?: string }>();
    Array.from(select.options).forEach((opt: any) => {
      catalog.set(opt.value, {
        value: opt.value,
        text: opt.text
      });
    });
    for (const opt of this.mergePreloadOptions(this.options?.options)) {
      catalog.set(opt.value, opt);
    }
    for (const opt of catalog.values()) {
      // clearOptions retains selected items; update them so descriptions survive refresh.
      if (tom.options[opt.value]) {
        tom.updateOption(opt.value, opt);
      } else {
        tom.addOption(opt);
      }
    }

    tom.refreshOptions(false);
    if (this.currentValue !== null && this.currentValue !== undefined) {
      tom.setValue(this.currentValue, true);
    }
  }

  writeValue(value: any): void {
    this.currentValue = value;
    if (this.tom) {
      this.tom.setValue(value, true);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (!this.tom) {
      return;
    }

    if (isDisabled) {
      this.tom.disable();
      return;
    }

    this.tom.enable();
  }

  private mergePreloadOptions(options: unknown): Array<{ value: string; text: string; description?: string }> {
    const merged = new Map<string, { value: string; text: string; description?: string }>();
    for (const item of Array.isArray(options) ? options : []) {
      const value = String((item as any)?.value ?? '').trim();
      const text = String((item as any)?.text ?? (item as any)?.label ?? value).trim();
      if (value) {
        merged.set(value, { value, text: text || value, description: String((item as any)?.description ?? '') });
      }
    }
    for (const item of this.preloadOptions || []) {
      const value = String(item?.value ?? '').trim();
      const text = String(item?.text ?? value).trim();
      if (value) {
        merged.set(value, { value, text: text || value, description: item.description });
      }
    }

    return Array.from(merged.values());
  }
}
